import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fmtDuration, parseClock, PUBLISH_PRIVACY_LABEL, PUBLISH_STATUS_TONE, THUMBNAIL_MAX_BYTES, type PublishPrivacy } from '@vtf/shared';
import { api, apiUpload, errorText } from '../lib/api';
import { fmtTime } from '../lib/format';
import { utcToZoned, zonedToUtc } from '../lib/live';
import { useMe } from '../lib/session';
import type { PublishList, PublishPackageView } from '../lib/publishing';
import { Button, Card, CardHeader, Empty, ErrorText, Field, Input, PageHeader, Pill, Segmented, Textarea } from '../components/ui';

type Scope = 'open' | 'done' | 'all';

/**
 * YouTube publishing queue (INT-REC-02): packages waiting for an approver first. Approvers (RECORDING_PUBLISH_APPROVER)
 * see every package; presenters see their own.
 */
export function PublishingQueue() {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const [scope, setScope] = useState<Scope>('open');
  const { data, error } = useQuery({ queryKey: ['publish-packages', scope], queryFn: () => api<PublishList>(`/publish-packages?scope=${scope}`), refetchInterval: 15_000 });
  return (
    <>
      <PageHeader title="YouTube publishing" sub={data ? (data.canApprove ? 'Nothing goes to YouTube until you approve it' : 'Your recordings on their way to YouTube; an approver signs each one off') : undefined} />
      <Segmented label="Show" value={scope} onChange={setScope} options={[{ value: 'open', label: 'In progress' }, { value: 'done', label: 'Published or rejected' }, { value: 'all', label: 'All' }]} />
      {error && <ErrorText error={error} />}
      <Card>
        {!data ? (
          <Empty>Loading…</Empty>
        ) : !data.packages.length ? (
          <Empty>{scope === 'open' ? 'Nothing is waiting. Webinar recordings get a package automatically once they are ready.' : 'Nothing here yet.'}</Empty>
        ) : (
          <ul className="m-0 flex list-none flex-col p-0">
            {data.packages.map((p) => (
              <li key={p.id} className="border-b border-line last:border-b-0" data-testid="publish-row">
                <Link to={`/publishing/${p.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3 text-ink no-underline hover:bg-raised">
                  {p.thumbnailUrl ? <img src={p.thumbnailUrl} alt="" className="aspect-video w-32 shrink-0 rounded-md object-cover" /> : <span className="flex aspect-video w-32 shrink-0 items-center justify-center rounded-md bg-raised text-xs text-muted">No thumbnail</span>}
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="font-semibold">{p.title}</span>
                    <span className="text-sm text-sub">
                      {p.presenter.name} · {p.source === 'WEBINAR' ? 'webinar' : 'room'} “{p.recordingTitle}” · {fmtTime(p.recordedAt, tz, true)}
                      {p.durationSeconds !== null ? ` · ${fmtDuration(p.durationSeconds)}` : ''}
                    </span>
                  </span>
                  <span className="flex flex-wrap items-center gap-2">
                    <Pill tone={PUBLISH_STATUS_TONE[p.status]}>{p.statusLabel}</Pill>
                    <Pill>{p.privacy === 'SCHEDULED' && p.publishAt ? `Scheduled ${fmtTime(p.publishAt, tz, true)}` : p.privacyLabel}</Pill>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

interface Form {
  title: string;
  description: string;
  tags: string;
  playlistId: string;
  privacy: PublishPrivacy;
  publishAtLocal: string;
  trimStart: string;
  trimEnd: string;
  thumbnailId: string | null;
}

const formOf = (p: PublishPackageView, tz: string): Form => ({
  title: p.title,
  description: p.description,
  tags: p.tags.join(', '),
  playlistId: p.playlistId ?? '',
  privacy: p.privacy,
  publishAtLocal: p.publishAt ? utcToZoned(p.publishAt, tz) : '',
  trimStart: p.trimStartSeconds !== null ? fmtDuration(p.trimStartSeconds) : '',
  trimEnd: p.trimEndSeconds !== null ? fmtDuration(p.trimEndSeconds) : '',
  thumbnailId: p.thumbnailId,
});

/** The body PUT /publish-packages/:id takes; times typed in the user's timezone are sent as UTC. */
function bodyOf(f: Form, tz: string) {
  const s = parseClock(f.trimStart);
  const e = parseClock(f.trimEnd);
  if (Number.isNaN(s) || Number.isNaN(e)) throw new Error('Use minutes:seconds for the trim, e.g. 1:30 or 1:02:05.');
  let publishAt: string | null = null;
  if (f.privacy === 'SCHEDULED') {
    const d = zonedToUtc(f.publishAtLocal, tz);
    if (!f.publishAtLocal || Number.isNaN(d.getTime())) throw new Error('Pick the date and time the video goes public.');
    publishAt = d.toISOString();
  }
  return {
    title: f.title,
    description: f.description,
    tags: f.tags.split(',').map((t) => t.trim()).filter(Boolean),
    playlistId: f.playlistId.trim() || null,
    privacy: f.privacy,
    publishAt,
    trimStartSeconds: s,
    trimEndSeconds: e,
    thumbnailId: f.thumbnailId,
  };
}

/** One package: preview, every field, thumbnails, the decision history and the approver's (or presenter's) actions. */
export function PublishPackagePage() {
  const { id = '' } = useParams();
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const { data, error } = useQuery({ queryKey: ['publish-package', id], queryFn: () => api<PublishPackageView>(`/publish-packages/${id}`), refetchInterval: (q) => (q.state.data && ['APPROVED', 'PUBLISHING'].includes(q.state.data.status) ? 3_000 : false) });
  if (error) return <Card className="p-4 text-down">{errorText(error)}</Card>;
  if (!data) return <Card className="p-4 text-sub">Loading…</Card>;
  // a fresh form whenever the package changes underneath (new version or status)
  return <PackageBody key={`${data.version}:${data.status}`} p={data} tz={tz} />;
}

function PackageBody({ p, tz }: { p: PublishPackageView; tz: string }) {
  const qc = useQueryClient();
  const [f, setF] = useState<Form>(() => formOf(p, tz));
  const [notes, setNotes] = useState('');
  const [asking, setAsking] = useState<null | 'changes' | 'reject'>(null);
  const set = (patch: Partial<Form>) => setF((x) => ({ ...x, ...patch }));
  const dirty = JSON.stringify(f) !== JSON.stringify(formOf(p, tz));
  const done = (v: PublishPackageView) => {
    qc.setQueryData(['publish-package', p.id], v);
    void qc.invalidateQueries({ queryKey: ['publish-packages'] });
  };
  const save = useMutation({ mutationFn: () => api<PublishPackageView>(`/publish-packages/${p.id}`, { method: 'PUT', body: bodyOf(f, tz) }), onSuccess: done });
  const approve = useMutation({
    mutationFn: async () => {
      const current = dirty ? await api<PublishPackageView>(`/publish-packages/${p.id}`, { method: 'PUT', body: bodyOf(f, tz) }) : p;
      return api<PublishPackageView>(`/publish-packages/${p.id}/approve`, { body: { version: current.version } });
    },
    onSuccess: done,
  });
  const decide = useMutation({
    mutationFn: (kind: 'changes' | 'reject') => api<PublishPackageView>(`/publish-packages/${p.id}/${kind === 'changes' ? 'request-changes' : 'reject'}`, { body: { notes } }),
    onSuccess: (v) => {
      setAsking(null);
      setNotes('');
      done(v);
    },
  });
  const resubmit = useMutation({
    mutationFn: async () => {
      if (dirty) await api(`/publish-packages/${p.id}`, { method: 'PUT', body: bodyOf(f, tz) });
      return api<PublishPackageView>(`/publish-packages/${p.id}/resubmit`, { body: { notes: notes || null } });
    },
    onSuccess: done,
  });
  const after = useMutation({ mutationFn: (a: 'publish' | 'reopen') => api<PublishPackageView>(`/publish-packages/${p.id}/${a}`, { body: {} }), onSuccess: done });
  const upload = useMutation({
    mutationFn: (file: File) => {
      if (!/^image\/(png|jpeg)$/.test(file.type)) throw new Error('Choose a PNG or JPEG image.');
      if (file.size > THUMBNAIL_MAX_BYTES) throw new Error('That image is larger than 2 MB (YouTube’s limit).');
      const form = new FormData();
      form.append('file', file);
      return apiUpload<PublishPackageView>(`/publish-packages/${p.id}/thumbnails`, form);
    },
    onSuccess: done,
  });
  const preview = useMutation({ mutationFn: () => api<{ url: string; expiresAt: string }>(`/publish-packages/${p.id}/preview`) });
  const busy = save.isPending || approve.isPending || decide.isPending || resubmit.isPending || after.isPending;
  const edit = p.can.edit;
  const latestChanges = [...p.decisions].reverse().find((d) => d.kind === 'CHANGES_REQUESTED');

  return (
    <>
      <PageHeader
        back={<Link to="/publishing" className="text-sm">← YouTube publishing</Link>}
        title={p.title}
        sub={<>
          {p.presenter.name} · {p.recording.webinarId ? <Link to={`/webinars/${p.recording.webinarId}`}>webinar “{p.recording.title}”</Link> : <>room “{p.recording.title}”</>} · recorded {fmtTime(p.recording.startedAt, tz, true)}
          {p.recording.durationSeconds !== null ? ` · ${fmtDuration(p.recording.durationSeconds)}` : ''}
        </>}
        right={<Pill tone={PUBLISH_STATUS_TONE[p.status]}>{p.statusLabel}</Pill>}
      />

      {p.status === 'PUBLISHED' && p.youtubeUrl && (
        <p role="status" className="m-0 rounded-lg bg-up-bg px-3 py-2 text-sm text-up" data-testid="published-banner">
          On YouTube: <a href={p.youtubeUrl} target="_blank" rel="noreferrer">{p.youtubeUrl}</a>
          {p.privacy === 'SCHEDULED' && p.publishAt ? ` · goes public ${fmtTime(p.publishAt, tz, true)}` : ` · ${p.youtubePrivacy ?? p.privacy.toLowerCase()}`}
        </p>
      )}
      {p.privacyForced && (
        <p role="alert" className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-sm text-warn" data-testid="privacy-forced">
          YouTube kept this video <strong>private</strong> although {PUBLISH_PRIVACY_LABEL[p.privacy].toLowerCase()} was approved. Until Google approves the floor’s API project in YouTube’s compliance audit, every upload through the API stays private. Change it to {PUBLISH_PRIVACY_LABEL[p.privacy].toLowerCase()} in YouTube Studio for now.
        </p>
      )}
      {(p.status === 'APPROVED' || p.status === 'PUBLISHING') && <p role="status" className="m-0 rounded-lg bg-accent-bg px-3 py-2 text-sm text-accent">Approved — publishing to YouTube{p.attempts > 1 ? ` (attempt ${p.attempts})` : ''}…{p.lastError ? ` Last try: ${p.lastError}` : ''}</p>}
      {p.status === 'FAILED' && (
        <Card tone="down" className="flex flex-col gap-2 p-4">
          <p role="alert" className="m-0 text-sm text-down">Publishing failed after {p.attempts} attempt{p.attempts === 1 ? '' : 's'}{p.lastError ? `: ${p.lastError}` : '.'}</p>
          {(p.can.retry || p.can.reopen) && (
            <span className="flex flex-wrap gap-2">
              {p.can.retry && <Button variant="primary" onClick={() => after.mutate('publish')} disabled={busy}>Try again</Button>}
              {p.can.reopen && <Button onClick={() => after.mutate('reopen')} disabled={busy}>Reopen for editing</Button>}
            </span>
          )}
        </Card>
      )}
      {p.status === 'REJECTED' && <p role="status" className="m-0 rounded-lg bg-raised px-3 py-2 text-sm text-sub">Rejected — the recording stays private in VTF.</p>}
      {p.status === 'CHANGES_REQUESTED' && latestChanges && (
        <p role="status" className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-sm text-warn" data-testid="change-notes">
          <strong>{latestChanges.actor?.name ?? 'The approver'} asked for changes:</strong> {latestChanges.notes}
        </p>
      )}
      {!p.youtube.configured && <p role="status" className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-sm text-warn">{p.youtube.message}</p>}
      {p.youtube.configured && !p.destination && p.status !== 'PUBLISHED' && <p role="status" className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-sm text-warn">No YouTube channel is connected. An Admin connects it in Administration → Streaming before this can be approved.</p>}
      <ErrorText error={save.error ?? approve.error ?? decide.error ?? resubmit.error ?? after.error ?? upload.error} />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Card className="flex flex-col gap-3 p-4">
          <CardHeader title="Video" />
          {p.status === 'PUBLISHED' || p.status === 'REJECTED' ? null : p.mode === 'UPDATE_LIVE' ? (
            <p className="m-0 text-sm text-sub" data-testid="mode-note">This session was streamed live to YouTube{p.destination ? ` (${p.destination.channel ?? p.destination.name})` : ''}, so approving <strong>updates that video</strong> — title, description, thumbnail and privacy — instead of uploading a second copy.</p>
          ) : (
            <p className="m-0 text-sm text-sub">Approving uploads the recording to {p.destination ? (p.destination.channel ?? p.destination.name) : 'the YouTube channel'}, sets the thumbnail{p.playlistId ? ', adds it to the playlist' : ''} and applies the privacy below.</p>
          )}
          {preview.data ? (
            <video controls preload="metadata" playsInline className="aspect-video w-full rounded-lg bg-black" src={`${preview.data.url}${p.trimStartSeconds !== null || p.trimEndSeconds !== null ? `#t=${p.trimStartSeconds ?? 0}${p.trimEndSeconds !== null ? `,${p.trimEndSeconds}` : ''}` : ''}`} data-testid="preview-video" />
          ) : (
            <div><Button onClick={() => preview.mutate()} disabled={preview.isPending}>▶ Preview the recording</Button></div>
          )}
          <ErrorText error={preview.error} />

          <span className="text-xs text-muted">Thumbnail</span>
          {!p.thumbnails.length && <p className="m-0 text-sm text-sub">{p.thumbnailsAvailable ? 'No thumbnails yet.' : 'Thumbnails can’t be generated on this server. Upload one below.'}</p>}
          <div role="radiogroup" aria-label="Thumbnail" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {p.thumbnails.map((t) => {
              const on = f.thumbnailId === t.id;
              return (
                <button key={t.id} type="button" role="radio" aria-checked={on} aria-label={t.label} disabled={!edit} onClick={() => set({ thumbnailId: t.id })} className={`flex cursor-pointer flex-col gap-1 rounded-lg border-2 bg-transparent p-1 text-left text-xs text-sub disabled:cursor-default ${on ? 'border-accent' : 'border-transparent'}`} data-testid="thumbnail-option">
                  <img src={t.url} alt={`${t.label} thumbnail`} className="aspect-video w-full rounded object-cover" />
                  <span>{on ? '✓ ' : ''}{t.label}</span>
                </button>
              );
            })}
          </div>
          {p.can.uploadThumbnail && (
            <Field label="Upload your own (PNG or JPEG, up to 2 MB, 1280×720 recommended)">
              <Input type="file" accept="image/png,image/jpeg" onChange={(e) => e.target.files?.[0] && upload.mutate(e.target.files[0])} disabled={upload.isPending} />
            </Field>
          )}
          <span className="text-xs text-muted">Generated from the {`brand template`} in three colour layouts (title, presenter and brand). A frame from the video isn’t used: that needs video tools the server doesn’t have.</span>
        </Card>

        <Card className="flex flex-col gap-3 p-4">
          <CardHeader title="YouTube details" />
          <Field label="Title" hint={`${f.title.length}/100`}><Input value={f.title} onChange={(e) => set({ title: e.target.value })} maxLength={100} readOnly={!edit} /></Field>
          <Field label="Description"><Textarea rows={8} value={f.description} onChange={(e) => set({ description: e.target.value })} readOnly={!edit} /></Field>
          <div className="flex flex-col gap-1 rounded-lg border border-line bg-raised p-3 text-xs text-sub" data-testid="disclaimer-preview">
            <span className="font-semibold text-muted">Added automatically below the description{p.disclaimer.version ? ` (disclaimer v${p.disclaimer.version})` : ''}:</span>
            <span className="whitespace-pre-wrap">{p.disclaimer.text}</span>
          </div>
          <Field label="Tags" hint="Separated by commas"><Input value={f.tags} onChange={(e) => set({ tags: e.target.value })} readOnly={!edit} /></Field>
          <Field label="Playlist" hint="Optional: the playlist id, after list= in its link"><Input value={f.playlistId} onChange={(e) => set({ playlistId: e.target.value })} placeholder="none" readOnly={!edit} /></Field>
          <span className="text-xs text-muted">Privacy</span>
          {edit ? (
            <Segmented label="Privacy" value={f.privacy} onChange={(v) => set({ privacy: v })} options={(['PUBLIC', 'UNLISTED', 'SCHEDULED'] as const).map((v) => ({ value: v, label: PUBLISH_PRIVACY_LABEL[v] }))} />
          ) : (
            <span className="text-sm">{PUBLISH_PRIVACY_LABEL[p.privacy]}{p.privacy === 'SCHEDULED' && p.publishAt ? ` · ${fmtTime(p.publishAt, tz, true)}` : ''}</span>
          )}
          {edit && f.privacy === 'SCHEDULED' && <Field label="Goes public at" hint={`Your time (${tz}); private on YouTube until then`}><Input type="datetime-local" value={f.publishAtLocal} onChange={(e) => set({ publishAtLocal: e.target.value })} /></Field>}
          <div className="grid grid-cols-2 gap-2">
            <Field label="Trim start (m:ss)"><Input value={f.trimStart} onChange={(e) => set({ trimStart: e.target.value })} placeholder="0:00" readOnly={!edit} /></Field>
            <Field label={`Trim end (of ${fmtDuration(p.recording.durationSeconds)})`}><Input value={f.trimEnd} onChange={(e) => set({ trimEnd: e.target.value })} placeholder="end" readOnly={!edit} /></Field>
          </div>
          <span className="text-xs text-muted" data-testid="trim-note">Trim applies inside VTF only: YouTube can’t trim on upload, so it gets the full recording. Viewers in the library start and stop at these times.</span>

          {edit && (
            <span className="flex flex-wrap gap-2 pt-1">
              {p.can.approve && <Button variant="primary" onClick={() => approve.mutate()} disabled={busy || !p.destination}>{dirty ? 'Save and approve' : 'Approve'}</Button>}
              {p.can.resubmit && <Button variant="primary" onClick={() => resubmit.mutate()} disabled={busy}>{dirty ? 'Save and resubmit' : 'Resubmit for approval'}</Button>}
              <Button onClick={() => save.mutate()} disabled={busy || !dirty}>Save changes</Button>
              {p.can.requestChanges && <Button onClick={() => setAsking('changes')} disabled={busy}>Request changes</Button>}
              {p.can.reject && <Button variant="danger" onClick={() => setAsking('reject')} disabled={busy}>Reject</Button>}
            </span>
          )}
          {!edit && p.can.reject && <span><Button variant="danger" onClick={() => setAsking('reject')} disabled={busy}>Reject</Button></span>}
          {asking && (
            <form
              className="flex flex-col gap-2 rounded-lg border border-line p-3"
              onSubmit={(e) => {
                e.preventDefault();
                decide.mutate(asking);
              }}
            >
              <Field label={asking === 'changes' ? 'What should the presenter change?' : 'Why reject it? (optional)'}><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} required={asking === 'changes'} /></Field>
              <span className="flex gap-2">
                <Button type="submit" variant={asking === 'reject' ? 'danger' : 'primary'} disabled={busy}>{asking === 'changes' ? 'Send back to the presenter' : 'Reject — keep it private'}</Button>
                <Button variant="ghost" onClick={() => setAsking(null)}>Cancel</Button>
              </span>
            </form>
          )}
          {p.can.resubmit && <Field label="Note for the approver (optional)"><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>}
        </Card>
      </div>

      <Card>
        <CardHeader title="History" />
        <ol className="m-0 flex list-none flex-col p-0">
          {p.decisions.map((d) => (
            <li key={d.id} className="flex flex-col gap-1 border-b border-line px-4 py-3 text-sm last:border-b-0" data-testid="decision-row">
              <span>
                <strong>{d.label}</strong> · {d.actor?.name ?? 'VTF'} · {fmtTime(d.createdAt, tz, true)} · version {d.version}
              </span>
              {d.notes && <span className="text-sub">“{d.notes}”</span>}
              <details className="text-xs text-muted">
                <summary className="cursor-pointer">Metadata at this point</summary>
                <span className="block whitespace-pre-wrap">
                  {d.snapshot.title} · {PUBLISH_PRIVACY_LABEL[d.snapshot.privacy]}{d.snapshot.publishAt ? ` ${fmtTime(d.snapshot.publishAt, tz, true)}` : ''} · tags: {d.snapshot.tags.join(', ') || '—'}{d.snapshot.playlistId ? ` · playlist ${d.snapshot.playlistId}` : ''}
                  {'\n\n'}
                  {d.snapshot.description}
                </span>
              </details>
            </li>
          ))}
        </ol>
      </Card>
    </>
  );
}
